import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { CategoriesPanel } from './components/CategoriesPanel';
import { MenuItemsPanel } from './components/MenuItemsPanel';
import { CustomerMenu } from './components/CustomerMenu';
import { CustomerHome } from './components/CustomerHome';
import { TakeawayPackagesPanel } from './components/TakeawayPackagesPanel';
import { TakeawayPackagesProvider } from './components/TakeawayPackagesContext';
import { ModifiersPanel } from './components/ModifiersPanel';
import { ModifiersProvider } from './components/ModifiersContext';
import { QuestionsPanel } from './components/QuestionsPanel';
import { QuestionsProvider } from './components/QuestionsContext';
import { OutletsProvider } from './components/OutletsContext';
import { CustomerAuthProvider } from './components/CustomerAuthContext';
import { CartProvider } from './components/CartContext';
import { CustomerAuth } from './components/CustomerAuth';
import { ReportPage } from './components/ReportPage';
import { OrdersPage } from './components/OrdersPage';
import { OrdersProvider } from './components/OrdersContext';
import { KitchenPage } from './components/KitchenPage';
import { BranchesPage } from './components/BranchesPage';
import { BranchesProvider } from './components/BranchesContext';
import { SettingsPage } from './components/SettingsPage';
import { SettingsProvider } from './components/SettingsContext';
import { CheckoutPreview } from './components/CheckoutPreview';
import { CRMPage } from './components/CRMPage';
import { CRMProvider } from './components/CRMContext';
import { CategoriesProvider } from './components/CategoriesContext';
import { MenusProvider } from './components/MenusContext';
import { MenusPanel } from './components/MenusPanel';
import { TabsProvider } from './components/TabsContext';
import { WaiterPage } from './components/WaiterPage';
import { StockProvider } from './components/StockContext';
import { PromotionsProvider } from './components/PromotionsContext';
import { PromotionsPage } from './components/PromotionsPage';
import { QrSessionProvider } from './components/QrSessionContext';
import { FeedbackProvider } from './components/FeedbackContext';
import { FeedbackAdminPage } from './components/FeedbackAdminPage';
import { CustomerFeedbackPage } from './components/CustomerFeedbackPage';
import { MoreVertical, Plus, Upload, Eye, ExternalLink, Menu } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
function MenuManagement() {
  const init = useScreenInit() || {};
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(init.selectedCategoryId ?? 'cat_savory');
  const [activeMainTab, setActiveMainTab] = useState('Main');
  const [activeSubTab, setActiveSubTab] = useState('Items');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-0">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-1">
        <div className="flex items-center gap-3" data-id="element-2">
          <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-3">
            <Menu size={24} data-id="element-4" />
          </button>
          <div className="flex items-center gap-2" data-id="element-5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-red-500 to-green-600 flex items-center justify-center shadow-lg" data-id="element-6">
              <span className="text-white text-sm" data-id="element-7">🦁</span>
            </div>
            <div className="leading-none" data-id="element-8">
              <div className="text-red-500 font-bold text-lg tracking-wide" data-id="element-9">
                QR
              </div>
            </div>
          </div>
        </div>
        <Link to="/menu" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors" data-id="element-10">
          <Eye size={20} data-id="element-11" />
        </Link>
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-12" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-13">
        <div className="px-4 md:px-8 pt-6 md:pt-8 pb-4 flex-shrink-0" data-id="element-14">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 md:mb-8 gap-4" data-id="element-15">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-16">
              Menu Management
            </h1>
            <Link to="/menu" target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-white border border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-colors shadow-sm whitespace-nowrap" data-id="element-17">
              <Eye size={16} data-id="element-18" />
              Preview Live Menu
              <ExternalLink size={14} className="text-orange-400" data-id="element-19" />
            </Link>
          </div>

          {/* Sub Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-4" data-id="element-20">
            <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-2 sm:pb-0 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-21">
              <button className={`px-4 py-2 text-sm font-medium border-b-2 sm:border-b-2 sm:rounded-none rounded-lg transition-colors ${activeSubTab === 'Items' ? 'border-orange-500 text-orange-500 sm:bg-transparent bg-orange-50' : 'border-transparent text-gray-600 hover:text-gray-900'}`} onClick={() => setActiveSubTab('Items')} data-id="element-22">
                Items
              </button>
              <button className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeSubTab === 'Menus' ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setActiveSubTab('Menus')} data-id="element-23">
                Menus
              </button>
              <button className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeSubTab === 'Modifiers' ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setActiveSubTab('Modifiers')} data-id="element-24">
                Modifiers
              </button>
              <button className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeSubTab === 'Questions' ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setActiveSubTab('Questions')} data-id="element-25">
                Questions
              </button>
              <button className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeSubTab === 'Takeaway Packages' ? 'bg-gray-200 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setActiveSubTab('Takeaway Packages')} data-id="element-26">
                Takeaway Packages
              </button>
            </div>

            <button className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors w-full sm:w-auto py-2 sm:py-0 border border-gray-200 sm:border-none rounded-lg sm:rounded-none" data-id="element-27">
              <Upload size={16} data-id="element-28" />
              Batch Upload
            </button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-8" data-id="element-29">
          {activeSubTab === 'Items' && <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6 md:gap-8 items-start" data-id="element-30">
              <CategoriesPanel selectedId={selectedCategoryId} onSelect={setSelectedCategoryId} data-id="element-31" />
              <MenuItemsPanel categoryName="Savory Plates" data-id="element-32" />
            </div>}
          {activeSubTab === 'Menus' && <MenusPanel data-id="element-33" />}
          {activeSubTab === 'Takeaway Packages' && <div className="max-w-3xl mx-auto" data-id="element-34">
              <TakeawayPackagesPanel data-id="element-35" />
            </div>}
          {activeSubTab === 'Modifiers' && <div className="max-w-3xl mx-auto" data-id="element-36">
              <ModifiersPanel data-id="element-37" />
            </div>}
          {activeSubTab === 'Questions' && <div className="max-w-3xl mx-auto" data-id="element-38">
              <QuestionsPanel data-id="element-39" />
            </div>}
        </div>
      </main>
    </div>;
}
export function App() {
  return <BrowserRouter data-id="element-40">
      <SettingsProvider data-id="element-41">
        <BranchesProvider data-id="element-42">
          <OrdersProvider data-id="element-43">
            <TabsProvider data-id="element-44">
              <CRMProvider data-id="element-45">
                <CategoriesProvider data-id="element-46">
                  <MenusProvider data-id="element-47">
                    <ModifiersProvider data-id="element-48">
                      <QuestionsProvider data-id="element-49">
                        <TakeawayPackagesProvider data-id="element-50">
                          <OutletsProvider data-id="element-51">
                            <CustomerAuthProvider data-id="element-52">
                              <QrSessionProvider data-id="element-53">
                                <CartProvider data-id="element-54">
                                  <StockProvider data-id="element-55">
                                    <PromotionsProvider data-id="element-56">
                                      <FeedbackProvider data-id="element-57">
                                        <Routes data-id="element-58">
                                          <Route path="/" element={<ReportPage data-id="element-60" />} data-id="element-59" />
                                          <Route path="/menu-management" element={<MenuManagement data-id="element-62" />} data-id="element-61" />
                                          <Route path="/welcome" element={<CustomerHome data-id="element-64" />} data-id="element-63" />
                                          <Route path="/menu" element={<CustomerMenu data-id="element-66" />} data-id="element-65" />
                                          <Route path="/login" element={<CustomerAuth data-id="element-68" />} data-id="element-67" />
                                          <Route path="/checkout" element={<CheckoutPreview data-id="element-70" />} data-id="element-69" />
                                          <Route path="/orders" element={<OrdersPage data-id="element-72" />} data-id="element-71" />
                                          <Route path="/kitchen" element={<KitchenPage data-id="element-74" />} data-id="element-73" />
                                          <Route path="/waiter" element={<WaiterPage data-id="element-76" />} data-id="element-75" />
                                          <Route path="/branches" element={<BranchesPage data-id="element-78" />} data-id="element-77" />
                                          <Route path="/settings" element={<SettingsPage data-id="element-80" />} data-id="element-79" />
                                          <Route path="/crm" element={<CRMPage data-id="element-82" />} data-id="element-81" />
                                          <Route path="/promotions" element={<PromotionsPage data-id="element-84" />} data-id="element-83" />
                                          <Route path="/reports" element={<ReportPage data-id="element-86" />} data-id="element-85" />
                                          <Route path="/feedback" element={<FeedbackAdminPage data-id="element-88" />} data-id="element-87" />
                                          <Route path="/leave-feedback" element={<CustomerFeedbackPage data-id="element-90" />} data-id="element-89" />
                                        </Routes>
                                      </FeedbackProvider>
                                    </PromotionsProvider>
                                  </StockProvider>
                                </CartProvider>
                              </QrSessionProvider>
                            </CustomerAuthProvider>
                          </OutletsProvider>
                        </TakeawayPackagesProvider>
                      </QuestionsProvider>
                    </ModifiersProvider>
                  </MenusProvider>
                </CategoriesProvider>
              </CRMProvider>
            </TabsProvider>
          </OrdersProvider>
        </BranchesProvider>
      </SettingsProvider>
    </BrowserRouter>;
}